import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthentication, AuthenticationStatus } from '@mysaiva/core/authentication'
import { LoadingPage } from '@mysaiva/core'
import { redirectToProduct, getAvailableProducts, type UserMeRead } from '../utils/productRedirect'
import { ErrorPage } from './ErrorPage'

/**
 * Component that handles post-login redirection logic:
 * 1. Waits for authentication status to load
 * 2. If not authenticated: redirects to /login
 * 3. If authenticated with products:
 *    - Checks localStorage for last selected product (key: 'mysaiva-last-app')
 *    - If last selected product is in user's product_list: redirects there
 *    - Otherwise: redirects to first product in user's product_list
 * 4. If authenticated with no products: shows ErrorPage
 *
 * The localStorage key 'mysaiva-last-app' is shared with the ProductSwitcher
 * component in @mysaiva/core to maintain consistency across the application.
 */
export function PostLoginRedirect() {
  const { user, status } = useAuthentication()
  const [shouldRedirect, setShouldRedirect] = useState(false)

  const isLoading = status === AuthenticationStatus.Loading

  useEffect(() => {
    if (!isLoading && user) {
      // Check if user has access to any products
      const availableProducts = getAvailableProducts(user as UserMeRead)

      if (availableProducts.length > 0) {
        setShouldRedirect(true)
        // Small delay to show loading state before redirect
        setTimeout(() => {
          redirectToProduct(user as UserMeRead)
        }, 500)
      }
    }
  }, [user, isLoading])

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingPage />
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Show loading screen while redirecting to product
  if (shouldRedirect) {
    return <LoadingPage />
  }

  // User is authenticated but has no products
  const availableProducts = getAvailableProducts(user as UserMeRead)
  if (availableProducts.length === 0) {
    return <ErrorPage />
  }

  // Fallback - should not reach here
  return <LoadingPage />
}
